/**
 * Retry a dynamic `import()` a few times before letting the failure surface.
 *
 * Chunk fetches fail far more often than the files are actually missing. The
 * dominant cause is a deploy: the backend reads the js/css URL list from a
 * manifest cached for 5s, so during a UI deploy there is a ~5s window where the
 * document references chunk hashes that are momentarily unavailable. The chunk
 * itself is present on the CDN, so simply re-issuing the request after that
 * window succeeds — webpack drops a failed chunk from its registry on error, so
 * a fresh `import()` re-hits the network for the chunk (CSS is inlined into the
 * JS chunks, see config-overrides.js, so there is no sibling CSS file).
 *
 * The fixed 3s delay × 2 retries (~6s total) is deliberately longer than the 5s
 * manifest cache, guaranteeing the cache has refreshed before the last attempt.
 * The last-resort reload (see `reloadOnChunkError`) still covers whatever is
 * still failing after that.
 */
export const retryImport = <T>(
  importFactory: () => Promise<T>,
  retries = 2,
  delayMs = 3000
): Promise<T> =>
  importFactory().catch((error: unknown) => {
    if (retries <= 0) {
      throw error;
    }

    return new Promise<T>((resolve, reject) => {
      window.setTimeout(() => {
        retryImport(importFactory, retries - 1, delayMs).then(resolve, reject);
      }, delayMs);
    });
  });
